import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Card, Stack, Typography } from '@mui/material';

import { useRemoveCatalogItemFromProcess } from '@/api/generated';
import DragIcon from '@/assets/icons/drag-vertical.svg?react';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { useDirection } from '@/hooks/useDirection';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders, transitions } from '@/theme/constants';
import typography from '@/theme/typography';

interface SortableCatalogItemCardProps {
  id: string;
  index: number;
  name: string;
  processId: string;
  onDelete?: () => void;
}

const SortableCatalogItemCard: FC<SortableCatalogItemCardProps> = ({ processId, id, name, index, onDelete }) => {
  const { t } = useTranslation(['settings', 'common']);
  const { end } = useDirection();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { showMessage } = useSnackbar();

  const { mutate: removeCatalogItem, isLoading: isDeleting } = useRemoveCatalogItemFromProcess();

  const handleDelete = () => {
    removeCatalogItem(
      { processId, catalogItemId: id },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          onDelete?.();
        },
        onError: error => {
          // eslint-disable-next-line no-console
          console.error('Error deleting test definition:', error);
          setIsDeleteModalOpen(false);
          showMessage(t('processSettings.form.tests.deleteError', { name }));
        }
      }
    );
  };

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        component={Stack}
        direction="row"
        alignItems="center"
        elevation={3}
        sx={{
          '&.MuiCard-root': {
            bgcolor: 'background.default',
            height: 40,
            border: borders.half,
            borderColor: 'grey.400',
            userSelect: 'none',
            ...style
          },
          '&:hover .DragIcon-root': {
            opacity: 1,
            width: 16
          }
        }}
      >
        <Stack
          sx={{
            height: '100%',
            width: 30,
            bgcolor: 'grey.200',
            justifyContent: 'center',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ fontSize: 24, color: 'primary.main', fontWeight: typography.fontWeightMedium }}>
            {index + 1}
          </Typography>
        </Stack>
        <Stack direction="row" flexGrow={1} alignItems="center" gap={0.5}>
          <Stack
            className="DragIcon-root"
            sx={{
              cursor: 'pointer',
              mx: 0.5,
              opacity: 0,
              width: 0,
              overflow: 'hidden',
              transition: transitions.compose(transitions.width_0_3, transitions.opacity_0_3)
            }}
            {...attributes}
            {...listeners}
          >
            <DragIcon />
          </Stack>
          <Typography variant="body2" sx={{ textAlign: 'start', py: 1 }}>
            {name}
          </Typography>
        </Stack>
        <Box>
          <DropdownMenu
            menuProps={{
              transformOrigin: {
                vertical: 'top',
                horizontal: end
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: end
              }
            }}
            autoClose
          >
            <DropdownMenuItem
              text={t('processSettings.form.tests.deleteButton')}
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={isDeleting}
            />
          </DropdownMenu>
        </Box>
      </Card>
      <ConfirmationModal
        open={isDeleteModalOpen}
        onConfirm={handleDelete}
        onDiscard={handleCancelDelete}
        title={t('processSettings.form.tests.removeConfirmTitle', { name })}
        confirmButtonLabel={t('processSettings.form.tests.confirmButton')}
        discardButtonLabel={t('processSettings.form.tests.discardButton')}
        isLoading={isDeleting}
      />
    </>
  );
};

export default SortableCatalogItemCard;
